import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, CheckCircle2, ArrowUp, ChevronRight, Volume2, VolumeX, Crosshair, Activity, Wind } from 'lucide-react';
const EP1_VIDEO = 'https://cdn.magicpatterns.com/uploads/9QN1iVmEQt5ED7bvqf6x43/Full%20Speed%20Foiling.mp4';
const EP2_VIDEO = 'https://cdn.magicpatterns.com/uploads/woKK9dL7uzSFoZTPEtCLEW/Lumpy%20and%20Bumpy%20in%20Barcelona.mp4';
// PLACEHOLDER: Replace this URL with your attached free music file URL
const BGM_URL = 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3';
const Header = ({
  step,
  isMuted,
  toggleMute
}: {
  step: number;
  isMuted: boolean;
  toggleMute: () => void;
}) => <div className="absolute top-8 left-8 right-8 flex justify-between items-center text-white z-50 pointer-events-none" data-id="element-0">
    <div className="flex items-center gap-2 bg-slate-950/60 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10" data-id="element-1">
      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" data-id="element-2" />
      <span className="text-xs font-bold tracking-widest uppercase text-blue-400" data-id="element-3">
        Race IQ
      </span>
    </div>
    <div className="flex items-center gap-2 pointer-events-auto" data-id="element-4">
      <button onClick={toggleMute} className="bg-slate-950/60 p-1.5 rounded-full backdrop-blur-md border border-white/10 hover:bg-slate-800 transition-colors cursor-pointer" data-id="element-5">
        {isMuted ? <VolumeX className="w-4 h-4" data-id="element-6" /> : <Volume2 className="w-4 h-4" data-id="element-7" />}
      </button>
      <div className="bg-slate-950/60 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10 pointer-events-none" data-id="element-8">
        <span className="text-xs font-bold opacity-90" data-id="element-9">{step}/9</span>
      </div>
    </div>
  </div>;
// Reusable bottom panel for content
const BottomPanel = ({
  children
}: {
  children: React.ReactNode;
}) => <motion.div initial={{
  y: 20,
  opacity: 0
}} animate={{
  y: 0,
  opacity: 1
}} className="bg-slate-950/95 backdrop-blur-xl rounded-t-[2rem] p-6 pb-8 pointer-events-auto border-t border-slate-800 shadow-[0_-10px_40px_rgba(0,0,0,0.5)] flex flex-col shrink-0" data-id="element-10">
    {children}
  </motion.div>;
// --- SCREENS ---
const Screen1 = ({
  onNext
}: any) => <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-11">
    <BottomPanel data-id="element-12">
      <div className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-3" data-id="element-13">
        America’s Cup Race IQ
      </div>
      <h1 className="text-3xl font-extrabold text-white mb-3 leading-[1.1]" data-id="element-14">
        Two details change how you see the race.
      </h1>
      <p className="text-slate-400 text-sm leading-relaxed mb-6" data-id="element-15">
        First, how the boat flies. Then, how crews keep it under control.
      </p>
      <button onClick={onNext} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 px-6 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-colors shadow-[0_0_30px_rgba(37,99,235,0.3)]" data-id="element-16">
        Start with lift <ChevronRight className="w-5 h-5" data-id="element-17" />
      </button>
    </BottomPanel>
  </div>;
const Screen2 = ({
  onNext
}: any) => <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-18">
    <div className="flex-1 relative" data-id="element-19">
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      delay: 0.5,
      duration: 1
    }} className="absolute top-[70%] left-[20%] right-[20%] h-1 bg-blue-500 shadow-[0_0_15px_#3b82f6] rounded-full" data-id="element-20" />
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1
    }} className="absolute top-[60%] left-[50%] -translate-x-1/2 flex flex-col items-center gap-1 text-blue-400 font-bold text-sm drop-shadow-lg" data-id="element-21">
        <ArrowUp className="w-5 h-5 animate-bounce" data-id="element-22" /> Lift
      </motion.div>
    </div>
    <BottomPanel data-id="element-23">
      <div className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-3" data-id="element-24">
        How foils work
      </div>
      <h2 className="text-2xl font-bold text-white mb-2" data-id="element-25">
        Watch the boat leave the water.
      </h2>
      <p className="text-slate-400 text-sm mb-6" data-id="element-26">
        That moment is where the speed story begins.
      </p>
      <button onClick={onNext} className="w-full bg-white text-slate-950 py-4 px-6 rounded-full font-bold text-lg flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors" data-id="element-27">
        Spot the lift <ChevronRight className="w-5 h-5" data-id="element-28" />
      </button>
    </BottomPanel>
  </div>;
const Screen3 = ({
  onNext
}: any) => {
  const [selected, setSelected] = useState<number | null>(null);
  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    setTimeout(() => onNext(idx === 1), 1500);
  };
  return <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-29">
      <div className="flex-1 relative" data-id="element-30">
        <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        repeat: Infinity,
        duration: 2
      }} className="absolute top-[65%] left-[45%] -translate-x-1/2 -translate-y-1/2 w-20 h-20 border-2 border-blue-500 rounded-full border-dashed" data-id="element-31" />
        <motion.div initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.5
      }} className="absolute top-[65%] left-[60%] bg-slate-900/80 text-white px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md border border-slate-700 flex items-center gap-1.5" data-id="element-32">
          <Crosshair className="w-3 h-3 text-blue-400" data-id="element-33" /> Look here
        </motion.div>
      </div>
      <BottomPanel data-id="element-34">
        <h2 className="text-xl font-bold text-white mb-4" data-id="element-35">
          What makes the boat rise out of the water?
        </h2>
        <div className="flex flex-col gap-2.5" data-id="element-36">
          {['A. The hull pushing upward', 'B. The foil creating lift', 'C. The crew leaning back'].map((opt, i) => {
          const isSelected = selected === i;
          const isCorrect = i === 1;
          let btnClass = 'bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800';
          if (selected !== null) {
            if (isSelected && isCorrect) btnClass = 'bg-blue-600/20 border-blue-500 text-white';else if (isSelected && !isCorrect) btnClass = 'bg-red-500/20 border-red-500 text-white';else if (!isSelected && isCorrect) btnClass = 'bg-blue-600/20 border-blue-500 text-white opacity-50';else btnClass = 'bg-slate-900 border-slate-800 text-slate-500 opacity-50';
          }
          return <button key={i} onClick={() => handleSelect(i)} className={`text-left p-4 rounded-2xl border-2 transition-all duration-300 ${btnClass}`} data-id="element-37">
                <div className="flex justify-between items-center" data-id="element-38">
                  <span className="font-bold text-sm" data-id="element-39">{opt}</span>
                  {isSelected && isCorrect && <CheckCircle2 className="text-blue-500 w-5 h-5" data-id="element-40" />}
                </div>
              </button>;
        })}
        </div>
      </BottomPanel>
    </div>;
};
const Screen4 = ({
  onNext,
  wasCorrect
}: any) => <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-41">
    <div className="flex-1 relative" data-id="element-42">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5
    }} className="absolute top-[40%] left-[15%] bg-blue-600/90 text-white px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md shadow-[0_0_15px_rgba(37,99,235,0.5)]" data-id="element-43">
        Less drag
      </motion.div>
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 1.0
    }} className="absolute top-[30%] right-[15%] bg-blue-600/90 text-white px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md shadow-[0_0_15px_rgba(37,99,235,0.5)]" data-id="element-44">
        More speed
      </motion.div>
    </div>
    <BottomPanel data-id="element-45">
      <h2 className={`text-xl font-bold mb-3 ${wasCorrect ? 'text-green-400' : 'text-white'}`} data-id="element-46">
        {wasCorrect ? 'Exactly. The foil creates lift.' : 'Not quite. The key is the foil.'}
      </h2>
      <p className="text-slate-300 text-sm leading-relaxed mb-6" data-id="element-47">
        The foil acts like an underwater wing. As speed builds, it creates lift,
        the hull rises, drag drops, and the boat can move faster.
      </p>
      <button onClick={onNext} className="w-full bg-white text-slate-950 py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors" data-id="element-48">
        Watch it fly <Play className="w-5 h-5 fill-current" data-id="element-49" />
      </button>
    </BottomPanel>
  </div>;
const Screen5 = ({
  onNext
}: any) => <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-50">
    <BottomPanel data-id="element-51">
      <h2 className="text-2xl font-bold text-white mb-3" data-id="element-52">
        Flight is only the beginning.
      </h2>
      <p className="text-slate-400 text-sm leading-relaxed mb-6" data-id="element-53">
        Once the boat is lifted, the next challenge is keeping speed under
        control.
      </p>
      <button onClick={onNext} className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 px-6 rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-colors shadow-[0_0_30px_rgba(37,99,235,0.3)]" data-id="element-54">
        Control the speed <ChevronRight className="w-5 h-5" data-id="element-55" />
      </button>
    </BottomPanel>
  </div>;
const Screen6 = ({
  onNext
}: any) => <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-56">
    <div className="flex-1 relative" data-id="element-57">
      <motion.div initial={{
      scaleX: 0
    }} animate={{
      scaleX: 1
    }} transition={{
      delay: 0.5,
      duration: 1
    }} className="absolute top-[60%] left-[10%] right-[10%] h-0.5 bg-blue-400/50 border-t border-dashed border-blue-300" data-id="element-58" />
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1
    }} className="absolute top-[55%] left-[15%] text-blue-300 text-[10px] font-bold uppercase tracking-wider bg-slate-900/80 px-2 py-1 rounded backdrop-blur-md" data-id="element-59">
        Balance
      </motion.div>
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.2
    }} className="absolute top-[55%] right-[15%] text-blue-300 text-[10px] font-bold uppercase tracking-wider bg-slate-900/80 px-2 py-1 rounded backdrop-blur-md" data-id="element-60">
        Control
      </motion.div>
    </div>
    <BottomPanel data-id="element-61">
      <div className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-3" data-id="element-62">
        How crews control speed
      </div>
      <h2 className="text-2xl font-bold text-white mb-2" data-id="element-63">
        At 50 knots, speed is not just about going faster.
      </h2>
      <p className="text-slate-400 text-sm mb-6" data-id="element-64">
        When the boat is flying, the crew has to manage balance, lift and
        control in every second.
      </p>
      <button onClick={onNext} className="w-full bg-white text-slate-950 py-4 px-6 rounded-full font-bold text-lg flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors" data-id="element-65">
        Test speed control <ChevronRight className="w-5 h-5" data-id="element-66" />
      </button>
    </BottomPanel>
  </div>;
const Screen7 = ({
  onNext
}: any) => {
  const [selected, setSelected] = useState<number | null>(null);
  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    setTimeout(() => onNext(idx === 1), 1500);
  };
  return <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-67">
      <div className="flex-1 relative" data-id="element-68">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        repeat: Infinity,
        duration: 1.5,
        repeatType: 'reverse'
      }} className="absolute top-[65%] left-[45%] w-4 h-4 bg-blue-500 rounded-full shadow-[0_0_20px_#3b82f6]" data-id="element-69" />
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.5
      }} className="absolute top-[75%] right-[30%] text-white text-xs font-bold bg-red-500/80 px-2 py-1 rounded backdrop-blur-md" data-id="element-70">
          Drag Zone
        </motion.div>
      </div>
      <BottomPanel data-id="element-71">
        <h2 className="text-xl font-bold text-white mb-2" data-id="element-72">
          What is the crew really controlling when the AC75 is flying?
        </h2>
        <p className="text-slate-400 text-xs mb-4" data-id="element-73">
          Speed comes from staying lifted, stable and efficient above the water.
        </p>
        <div className="flex flex-col gap-2.5" data-id="element-74">
          {['A. Only the direction of the boat', 'B. Lift, balance and drag', 'C. How hard the boat hits the waves'].map((opt, i) => {
          const isSelected = selected === i;
          const isCorrect = i === 1;
          let btnClass = 'bg-slate-900 border-slate-800 text-slate-300 hover:bg-slate-800';
          if (selected !== null) {
            if (isSelected && isCorrect) btnClass = 'bg-blue-600/20 border-blue-500 text-white';else if (isSelected && !isCorrect) btnClass = 'bg-red-500/20 border-red-500 text-white';else if (!isSelected && isCorrect) btnClass = 'bg-blue-600/20 border-blue-500 text-white opacity-50';else btnClass = 'bg-slate-900 border-slate-800 text-slate-500 opacity-50';
          }
          return <button key={i} onClick={() => handleSelect(i)} className={`text-left p-4 rounded-2xl border-2 transition-all duration-300 ${btnClass}`} data-id="element-75">
                <div className="flex justify-between items-center" data-id="element-76">
                  <span className="font-bold text-sm" data-id="element-77">{opt}</span>
                  {isSelected && isCorrect && <CheckCircle2 className="text-blue-500 w-5 h-5 shrink-0 ml-2" data-id="element-78" />}
                </div>
              </button>;
        })}
        </div>
      </BottomPanel>
    </div>;
};
const Screen8 = ({
  onNext,
  wasCorrect
}: any) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStep(1), 500);
    const t2 = setTimeout(() => setStep(2), 1000);
    const t3 = setTimeout(() => setStep(3), 1500);
    const t4 = setTimeout(() => setStep(4), 2000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  return <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-79">
      <div className="flex-1 relative" data-id="element-80">
        <AnimatePresence data-id="element-81">
          {step >= 1 && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="absolute top-[25%] left-[20%] bg-slate-900/80 backdrop-blur-md border border-slate-700 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1" data-id="element-82">
              <ArrowUp className="w-3 h-3 text-blue-400" data-id="element-83" /> Lift
            </motion.div>}
          {step >= 2 && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="absolute top-[40%] right-[20%] bg-slate-900/80 backdrop-blur-md border border-slate-700 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1" data-id="element-84">
              <Activity className="w-3 h-3 text-blue-400" data-id="element-85" /> Balance
            </motion.div>}
          {step >= 3 && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="absolute top-[55%] left-[25%] bg-slate-900/80 backdrop-blur-md border border-slate-700 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1" data-id="element-86">
              <Crosshair className="w-3 h-3 text-blue-400" data-id="element-87" /> Trim
            </motion.div>}
          {step >= 4 && <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="absolute top-[70%] right-[25%] bg-blue-600/90 backdrop-blur-md border border-blue-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-[0_0_15px_rgba(37,99,235,0.5)] flex items-center gap-1" data-id="element-88">
              <Wind className="w-3 h-3" data-id="element-89" /> Drag
            </motion.div>}
        </AnimatePresence>
      </div>
      <BottomPanel data-id="element-90">
        <h2 className={`text-xl font-bold mb-3 ${wasCorrect ? 'text-green-400' : 'text-white'}`} data-id="element-91">
          {wasCorrect ? 'Exactly. The crew manages what makes speed possible.' : 'Not quite. It is not just steering or pushing harder.'}
        </h2>
        <p className="text-slate-300 text-sm leading-relaxed mb-6" data-id="element-92">
          An AC75 is fastest when it stays lifted and stable. The crew works to
          keep the boat balanced, reduce drag and maintain control as conditions
          change.
        </p>
        <button onClick={onNext} className="w-full bg-white text-slate-950 py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors" data-id="element-93">
          See control points <Play className="w-5 h-5 fill-current" data-id="element-94" />
        </button>
      </BottomPanel>
    </div>;
};
const Screen9 = ({
  onReset
}: any) => {
  const [voted, setVoted] = useState<number | null>(null);
  const options = [{
    label: 'How wind decides the race',
    percent: 45
  }, {
    label: 'How teams film the action',
    percent: 25
  }, {
    label: 'How crews communicate',
    percent: 18
  }, {
    label: 'How tactics change in seconds',
    percent: 12
  }];
  return <div className="absolute inset-0 flex flex-col justify-end pointer-events-none" data-id="element-95">
      <div className="absolute inset-0 bg-slate-950/40 backdrop-blur-md" data-id="element-96" />
      <div className="relative z-10 p-6 pt-28 flex flex-col h-full pointer-events-auto" data-id="element-97">
        <h2 className="text-3xl font-extrabold text-white mb-3 leading-tight" data-id="element-98">
          What should we unlock next?
        </h2>
        <p className="text-slate-400 text-base mb-8" data-id="element-99">
          Choose the next 50-knot detail.
        </p>
        <div className="flex flex-col gap-3 mb-auto" data-id="element-100">
          {options.map((opt, i) => <button key={i} disabled={voted !== null} onClick={() => setVoted(i)} className="relative overflow-hidden w-full text-left p-4 rounded-2xl border border-slate-700 bg-slate-900/80 backdrop-blur-md hover:bg-slate-800 transition-colors group" data-id="element-101">
              {voted !== null && <motion.div initial={{
            width: 0
          }} animate={{
            width: `${opt.percent}%`
          }} transition={{
            duration: 1
          }} className={`absolute inset-0 ${voted === i ? 'bg-blue-600/40' : 'bg-slate-700/40'}`} data-id="element-102" />}
              <div className="relative flex justify-between items-center z-10" data-id="element-103">
                <span className={`font-bold text-sm ${voted === i ? 'text-white' : 'text-slate-300'}`} data-id="element-104">
                  {opt.label}
                </span>
                {voted !== null && <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.8
            }} className="text-white font-bold text-sm" data-id="element-105">
                    {opt.percent}%
                  </motion.span>}
              </div>
            </button>)}
        </div>
        <AnimatePresence data-id="element-106">
          {voted !== null && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1
        }} className="mt-6" data-id="element-107">
              <p className="text-blue-300 text-sm text-center font-medium mb-6" data-id="element-108">
                Every America’s Cup moment has a hidden system behind it.
              </p>
              <button onClick={onReset} className="w-full bg-white text-slate-950 py-4 rounded-full font-bold text-lg flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors" data-id="element-109">
                <Play className="w-5 h-5 fill-current" data-id="element-110" /> Watch next short
              </button>
            </motion.div>}
        </AnimatePresence>
        <p className="text-center text-slate-500 text-[10px] mt-8 uppercase tracking-wider font-medium" data-id="element-111">
          Source: Official America’s Cup short-form footage
        </p>
      </div>
    </div>;
};
// --- MAIN APP ---
export function App() {
  const [currentScreen, setCurrentScreen] = useState(1);
  const [quiz1Correct, setQuiz1Correct] = useState(false);
  const [quiz2Correct, setQuiz2Correct] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const bgmRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Determine which video to play based on the screen
  const currentVideoSrc = currentScreen <= 5 ? EP1_VIDEO : EP2_VIDEO;
  useEffect(() => {
    if (bgmRef.current) {
      if (isMuted || currentScreen === 9) {
        bgmRef.current.pause();
      } else {
        bgmRef.current.play().catch(() => {});
      }
    }
  }, [isMuted, currentScreen]);
  const toggleMute = () => setIsMuted(!isMuted);
  const nextScreen = () => setCurrentScreen(p => Math.min(p + 1, 9));
  const reset = () => {
    setCurrentScreen(1);
    setQuiz1Correct(false);
    setQuiz2Correct(false);
  };
  const handleQuiz1Answer = (correct: boolean) => {
    setQuiz1Correct(correct);
    nextScreen();
  };
  const handleQuiz2Answer = (correct: boolean) => {
    setQuiz2Correct(correct);
    nextScreen();
  };
  const screens = [<Screen1 key="1" onNext={nextScreen} data-id="element-112" />, <Screen2 key="2" onNext={nextScreen} data-id="element-113" />, <Screen3 key="3" onNext={handleQuiz1Answer} data-id="element-114" />, <Screen4 key="4" onNext={nextScreen} wasCorrect={quiz1Correct} data-id="element-115" />, <Screen5 key="5" onNext={nextScreen} data-id="element-116" />, <Screen6 key="6" onNext={nextScreen} data-id="element-117" />, <Screen7 key="7" onNext={handleQuiz2Answer} data-id="element-118" />, <Screen8 key="8" onNext={nextScreen} wasCorrect={quiz2Correct} data-id="element-119" />, <Screen9 key="9" onReset={reset} data-id="element-120" />];
  return <div className="min-h-screen bg-slate-950 flex items-center justify-center font-sans selection:bg-blue-500/30" data-id="element-121">
      <audio ref={bgmRef} src={BGM_URL} loop data-id="element-122" />

      <div className="w-full max-w-md h-[100dvh] sm:h-[850px] sm:max-h-[90vh] bg-slate-950 relative overflow-hidden sm:rounded-[2.5rem] sm:border-[6px] border-slate-800 shadow-[0_0_50px_rgba(0,0,0,0.5)]" data-id="element-123">
        {/* GLOBAL BACKGROUND VIDEO */}
        <video key={currentVideoSrc} // Forces recreation when source changes
      ref={videoRef} src={currentVideoSrc} autoPlay loop muted={isMuted} playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-124" />

        <Header step={currentScreen} isMuted={isMuted} toggleMute={toggleMute} data-id="element-125" />

        <AnimatePresence initial={false} mode="wait" data-id="element-126">
          <motion.div key={currentScreen} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 1.02
        }} transition={{
          duration: 0.3
        }} className="absolute inset-0" data-id="element-127">
            {screens[currentScreen - 1]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}